import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

const SIDEBAR_STORAGE_KEY = 'schwrz.home.sidebarOpen';

function readStoredSidebarOpen(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

/**
 * Client-side-only UI state for the Home shell: companies sidebar visibility,
 * the "show remaining (unpinned) companies" toggle and the currently selected
 * employee (drives the by-user multi-project task view).
 */
export const useHomeViewStore = defineStore('homeView', () => {
  const sidebarOpen = ref(readStoredSidebarOpen());
  const showUnpinned = ref(false);
  const selectedUserId = ref<string | null>(null);
  /** True when user explicitly cleared the by-user filter (company/project overview). */
  const userFilterCleared = ref(false);

  watch(sidebarOpen, (open) => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, open ? 'true' : 'false');
    } catch {
      /* ignore quota / privacy-mode failures */
    }
  });

  function toggleSidebar(): void {
    sidebarOpen.value = !sidebarOpen.value;
  }

  function toggleShowUnpinned(): void {
    showUnpinned.value = !showUnpinned.value;
  }

  function selectUser(id: string | null): void {
    selectedUserId.value = id;
    userFilterCleared.value = id === null;
  }

  /** On first Home visit, show the session user's assigned tasks (legacy bootstrap). */
  function selectCurrentUserIfNone(workspaceUserId: string | null | undefined): boolean {
    if (selectedUserId.value !== null || userFilterCleared.value || !workspaceUserId) return false;
    selectedUserId.value = workspaceUserId;
    return true;
  }

  return {
    sidebarOpen,
    showUnpinned,
    selectedUserId,
    toggleSidebar,
    toggleShowUnpinned,
    selectUser,
    selectCurrentUserIfNone,
  };
});
